import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Table,
    Badge,
    Spinner,
    Alert,
    Button,
} from "reactstrap";
import { useAuth } from "../../Provider/AuthProvider";
import { useNavigate, Link } from "react-router-dom";
import StockCorrectionModal from "./StockCorrectionModal";

interface WarehouseValuation {
    id: number;
    ref: string;
    lieu: string;
    description: string;
    total_qty: number;
    nb_products: number;
    total_value_ht: number;
    total_value_ttc: number;
}

interface StockMovement {
    rowid: number;
    datem: string;
    qty: number;
    label: string;
    inventorycode: string;
    price: number;
    type_mouvement: number;
    origintype: string | null;
    fk_origin: number | null;
    batch: string | null;
    product_id: number | null;
    product_ref: string | null;
    product_label: string | null;
    product_barcode: string | null;
    entrepot_id: number;
    entrepot_ref: string;
    user_id: number | null;
    user_login: string | null;
    user_firstname: string | null;
    user_lastname: string | null;
}

interface MonStockResponse {
    success: boolean;
    message: string;
    warehouses: WarehouseValuation[];
    last_movements: StockMovement[];
    entity: number;
    entityname: string;
}

const API_URL = "https://api.apapiecesauto.fr/apiV2/";

const formatEuro = (v: number) =>
    new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);

const formatDate = (d: string) => {
    if (!d) return "-";
    const date = new Date(d.replace(" ", "T"));
    if (isNaN(date.getTime())) return d;
    return date.toLocaleString("fr-FR");
};

const MonStock = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [warehouses, setWarehouses] = useState<WarehouseValuation[]>([]);
    const [movements, setMovements] = useState<StockMovement[]>([]);
    const [stockCorrectionModalOpen, setStockCorrectionModalOpen] = useState(false);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            const response: any = await axios.post<MonStockResponse>(
                API_URL,
                {
                    action: "monstock",
                    sub_action: "full",
                    entity: user.entity,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${user.token}`,
                    },
                }
            );

            const d = response;
            if (d.success) {
                setWarehouses(d.warehouses || []);
                setMovements(d.last_movements || []);
            } else {
                throw new Error(d.message || "Erreur de chargement");
            }
        } catch (e: any) {
            setError(e.message || "Erreur lors du chargement");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.token) fetchData();
    }, [user?.token, user?.entity]);

    const totals = warehouses.reduce(
        (acc, w) => ({
            qty: acc.qty + (w.total_qty || 0),
            ht: acc.ht + (w.total_value_ht || 0),
            ttc: acc.ttc + (w.total_value_ttc || 0),
            nb: acc.nb + (w.nb_products || 0),
        }),
        { qty: 0, ht: 0, ttc: 0, nb: 0 }
    );

    const movementBadge = (qty: number) =>
        qty >= 0 ? (
            <Badge color="success">+{qty}</Badge>
        ) : (
            <Badge color="danger">{qty}</Badge>
        );

    return (
        <div className="page-content">
            <Container fluid>
                <Row className="mb-3">
                    <Col>
                        <h4 className="mb-0">Mon stock</h4>
                        <small className="text-muted">
                            Valorisation au prix de vente et derniers mouvements
                        </small>
                    </Col>
                    <Col xs="auto" className="d-flex gap-2">
                        <Button color="warning" onClick={() => setStockCorrectionModalOpen(true)}>
                            <i className="ri-stock-line align-bottom me-2"></i>
                            Corriger Stock
                        </Button>
                        <Button color="light" onClick={fetchData} disabled={loading}>
                            <i className="ri-refresh-line align-bottom me-1"></i>
                            Rafraîchir
                        </Button>
                    </Col>
                </Row>

                {error && <Alert color="danger">{error}</Alert>}

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                    </div>
                ) : (
                    <>
                        <Card className="mb-4">
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <span>
                                    <i className="ri-store-2-line align-bottom me-2"></i>
                                    Entrepôts ({warehouses.length})
                                </span>
                                <span className="text-muted small">
                                    Total HT: <strong>{formatEuro(totals.ht)}</strong> —
                                    Total TTC: <strong>{formatEuro(totals.ttc)}</strong>
                                </span>
                            </CardHeader>
                            <CardBody className="p-0">
                                <div className="table-responsive">
                                    <Table className="align-middle mb-0" hover>
                                        <thead className="table-light">
                                            <tr>
                                                <th>Réf</th>
                                                <th>Lieu</th>
                                                <th className="text-end">Produits</th>
                                                <th className="text-end">Quantité</th>
                                                <th className="text-end">Valeur HT</th>
                                                <th className="text-end">Valeur TTC</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {warehouses.length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} className="text-center text-muted py-4">
                                                        Aucun entrepôt
                                                    </td>
                                                </tr>
                                            ) : (
                                                warehouses.map((w) => (
                                                    <tr key={w.id}>
                                                        <td><strong>{w.ref}</strong></td>
                                                        <td className="text-muted">{w.lieu || "-"}</td>
                                                        <td className="text-end">{w.nb_products}</td>
                                                        <td className="text-end">{w.total_qty}</td>
                                                        <td className="text-end">{formatEuro(w.total_value_ht)}</td>
                                                        <td className="text-end">{formatEuro(w.total_value_ttc)}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                        </Card>

                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <span>
                                    <i className="ri-exchange-line align-bottom me-2"></i>
                                    20 derniers mouvements
                                </span>
                                <Link to="/mouvements" className="btn btn-sm btn-primary">
                                    Voir tous les mouvements
                                    <i className="ri-arrow-right-line align-bottom ms-1"></i>
                                </Link>
                            </CardHeader>
                            <CardBody className="p-0">
                                <div className="table-responsive">
                                    <Table className="align-middle mb-0" hover>
                                        <thead className="table-light">
                                            <tr>
                                                <th>Date</th>
                                                <th>Entrepôt</th>
                                                <th>Produit</th>
                                                <th>Code-barres</th>
                                                <th className="text-end">Qté</th>
                                                <th>Libellé</th>
                                                <th>Utilisateur</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {movements.length === 0 ? (
                                                <tr>
                                                    <td colSpan={7} className="text-center text-muted py-4">
                                                        Aucun mouvement
                                                    </td>
                                                </tr>
                                            ) : (
                                                movements.map((m) => (
                                                    <tr key={m.rowid}>
                                                        <td>{formatDate(m.datem)}</td>
                                                        <td>{m.entrepot_ref}</td>
                                                        <td>
                                                            <div><strong>{m.product_ref || "-"}</strong></div>
                                                            <small className="text-muted">{m.product_label}</small>
                                                        </td>
                                                        <td>{m.product_barcode || m.batch || "-"}</td>
                                                        <td className="text-end">{movementBadge(m.qty)}</td>
                                                        <td>{m.label || "-"}</td>
                                                        <td>
                                                            {m.user_firstname || m.user_lastname
                                                                ? `${m.user_firstname ?? ""} ${m.user_lastname ?? ""}`.trim()
                                                                : m.user_login || "-"}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                        </Card>
                    </>
                )}
            </Container>

            <StockCorrectionModal
                isOpen={stockCorrectionModalOpen}
                toggle={() => setStockCorrectionModalOpen(false)}
            />
        </div>
    );
};

export default MonStock;
